"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useLocalStorageUtils = exports.localStorageKey = exports.DISMISSIBLE_LOGGED_OUT_USER_ID = exports.useDismissibleContext = exports.DismissibleProvider = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const Yup = __importStar(require("yup"));
const react_1 = require("react");
const lodash_uniqby_1 = __importDefault(require("lodash.uniqby"));
const DismissibleContext = (0, react_1.createContext)({
    dismissed: [],
    keys: [],
    isDismissed: () => ({ status: false, timestamp: 0 }),
});
const DismissibleProvider = ({ children, keys = [], userID }) => {
    const id = userID !== null && userID !== void 0 ? userID : exports.DISMISSIBLE_LOGGED_OUT_USER_ID;
    const [dismissed, setDismissed] = (0, react_1.useState)([]);
    const localStorageUtils = (0, exports.useLocalStorageUtils)({ keys });
    const { __dismiss__, get } = localStorageUtils;
    const dismiss = (0, react_1.useCallback)((key) => {
        const keys = Array.isArray(key) ? key : [key];
        const timestamp = Date.now();
        __dismiss__(id, timestamp, keys);
        setDismissed((prevDismissed) => {
            return (0, lodash_uniqby_1.default)([...prevDismissed, ...keys.map((k) => ({ key: k, timestamp }))], (d) => d.key);
        });
    }, [id]);
    (0, react_1.useEffect)(() => {
        setDismissed(get(id));
    }, [id]);
    const mounted = useDidMount();
    const isDismissed = (0, react_1.useCallback)((key) => {
        if (!mounted) {
            return {
                status: false,
                timestamp: 0,
            };
        }
        const dismissedKey = dismissed.find((d) => d.key === key);
        return dismissedKey
            ? { status: true, timestamp: dismissedKey.timestamp }
            : { status: false, timestamp: 0 };
    }, [dismissed, mounted]);
    /**
     * If the user is logged out, and performs some action which causes them
     * to login, we need to sync up the dismissed state from the logged out user
     */
    const syncFromLoggedOutUser = (0, react_1.useCallback)(() => {
        if (id === exports.DISMISSIBLE_LOGGED_OUT_USER_ID) {
            return;
        }
        const loggedOutDismissals = get(exports.DISMISSIBLE_LOGGED_OUT_USER_ID);
        const loggedInDismissals = get(id);
        const dismissals = (0, lodash_uniqby_1.default)([...loggedOutDismissals, ...loggedInDismissals], (d) => d.key);
        setDismissed(dismissals);
        localStorage.setItem((0, exports.localStorageKey)(id), JSON.stringify(dismissals));
        localStorage.removeItem((0, exports.localStorageKey)(exports.DISMISSIBLE_LOGGED_OUT_USER_ID));
    }, [id]);
    // Ensure that the dismissed state stays in sync incase the user
    // has multiple tabs open.
    (0, react_1.useEffect)(() => {
        const current = get(id);
        if (current.length === 0)
            return;
        const handleFocus = () => {
            setDismissed(current);
        };
        window.addEventListener("focus", handleFocus);
        return () => {
            window.removeEventListener("focus", handleFocus);
        };
    }, [id]);
    return ((0, jsx_runtime_1.jsx)(DismissibleContext.Provider, Object.assign({ value: {
            dismissed,
            dismiss,
            isDismissed,
            keys,
            syncFromLoggedOutUser,
        } }, { children: children })));
};
exports.DismissibleProvider = DismissibleProvider;
const useDismissibleContext = () => {
    return (0, react_1.useContext)(DismissibleContext);
};
exports.useDismissibleContext = useDismissibleContext;
exports.DISMISSIBLE_LOGGED_OUT_USER_ID = "user";
const localStorageKey = (id) => {
    return `progressive-onboarding.dismissed.${id}`;
};
exports.localStorageKey = localStorageKey;
const useLocalStorageUtils = ({ keys }) => {
    const schema = Yup.object().shape({
        key: Yup.string().oneOf([...keys]),
        timestamp: Yup.number(),
    });
    const isValid = (value) => {
        return schema.isValidSync(value);
    };
    const parse = (value) => {
        if (!value)
            return [];
        try {
            const parsed = JSON.parse(value);
            return parsed.filter((obj) => {
                return isValid(obj) && keys.includes(obj.key);
            });
        }
        catch (err) {
            return [];
        }
    };
    const __dismiss__ = (id, timestamp, key) => {
        const keys = Array.isArray(key) ? key : [key];
        keys.forEach((key) => {
            const item = localStorage.getItem((0, exports.localStorageKey)(id));
            const dismissed = parse(item);
            localStorage.setItem((0, exports.localStorageKey)(id), JSON.stringify((0, lodash_uniqby_1.default)([...dismissed, { key, timestamp }], ({ key }) => key)));
        });
    };
    const get = (id) => {
        const item = localStorage.getItem((0, exports.localStorageKey)(id));
        return parse(item);
    };
    const reset = (id) => {
        localStorage.removeItem((0, exports.localStorageKey)(id));
    };
    return {
        __dismiss__,
        get,
        isValid,
        parse,
        reset,
        schema,
    };
};
exports.useLocalStorageUtils = useLocalStorageUtils;
function useDidMount(defaultMounted = false) {
    const [isMounted, toggleMounted] = (0, react_1.useState)(defaultMounted);
    (0, react_1.useEffect)(() => {
        toggleMounted(true);
    }, []);
    return isMounted;
}
//# sourceMappingURL=DismissibleContext.js.map